import React from "react";
import styled from "styled-components";
import Sit from "../../assets/sit.svg";
import { Bar } from "react-chartjs-2";
import { generalProgrammingData, webDevelopmentData } from "../../utils/chart";
import { coffee_drank_count, project_done_count } from "../../config";
import AnimatedNumber from "animated-number-react";

const StyledBackgroundSection = styled.section`
  position: relative;
  background: var(--white);
  color: var(--black-dark);
`;

const StyleBackgroundBody = styled.div`
  grid-template-columns: 100%;

  @media (min-width: 1024px) {
    grid-template-columns: 50% 50% !important;
  }
`;

const StyledBackgroundContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: space-between;

  .sit-figure {
    display: none;
  }

  .metrics {
    display: none;
  }

  .background-title {
    margin-bottom: var(--mb-3);
  }

  p {
    font-size: var(--normal-font-size);
    font-family: var(--body-font-2);
    margin-bottom: var(--mb-2);
    line-height: 24px;
  }

  .scale {
    font-family: var(--body-font-2);
    font-size: var(--normal-font-size);
    font-weight: var(--font-semi);
    color: var(--black-regular);
    li {
      margin-bottom: var(--mb-1);
    }
  }

  .metric-label {
    margin-bottom: var(--mb-1);
  }

  .metric-value {
    font-size: var(--bigger-font-size);
    font-weight: var(--font-bold);
  }

  @media (min-width: 1024px) {
    text-align: initial;

    p {
      max-width: 70%;
    }

    .sit-figure {
      display: block;
      height: 300px;
      width: 220px;
    }

    .metric-value {
      font-size: var(--big-font-size);
      font-weight: var(--font-bold);
    }

    .metrics {
      min-width: 200px;
      display: flex;
      flex-direction: row;
      gap: 40px;
      position: absolute;
      background: transparent;
      top: 50%;
      padding: var(--mb-2);
      left: 50%;

      &::before {
        content: "";
        position: absolute;
        display: block;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        border: 3px solid var(--black-dark);
        border-radius: var(--border-radius);
        box-sizing: border-box;
        clip-path: polygon(
          0% 105%,
          0% 0%,
          105% 0%,
          105% 105%,
          43px 105%,
          43px 80%,
          21px 80%,
          21px 105%
        );
        border-bottom-width: 6px;
      }

      &::after {
        content: "";
        position: absolute;
        display: block;
        width: 25px;
        height: 20px;
        background: var(--black-dark);
        top: calc(100% - 6px);
        left: 20px;
        box-sizing: border-box;
        clip-path: polygon(
          0 0,
          0% 100%,
          150% 0%,
          calc(100% - 2px) 0,
          1px calc(100% - 2px),
          2px 0
        );
      }
    }
  }

  @media (min-width: 1350px) {
    .sit-figure {
      display: block;
      height: 450px;
      width: 350px;
    }

    .metrics {
      left: 40%;
      top: 50%;
    }

    .metric-value {
      font-size: var(--bigger-font-size);
      font-weight: var(--font-bold);
    }
  }
`;

const StyledBackgroundGraphs = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: center;

  .graph-box {
    margin-bottom: var(--mb-6);
  }

  .graph-label {
    margin-bottom: var(--mb-3);
  }

  .mobile-metrics {
    display: flex;
    flex-direction: row;
    margin-bottom: var(--mb-2);
    gap: 20px;
    margin-left: var(--mb-2);
    margin-right: var(--mb-2);
  }

  .mobile-metric-label {
    margin-bottom: var(--mb-1);
  }

  .mobile-metric-value {
    font-size: var(--bigger-font-size);
    font-weight: var(--font-bold);
  }

  .cmobile-metrics-body {
    text-align: center;
  }

  @media (min-width: 1024px) {
    text-align: initial;

    .mobile-metrics {
      display: none !important;
    }
  }
`;

const graphOptions = {
  indexAxis: "y",
  hoverBackgroundColor: "#F57E00",
  layout: {
    padding: {
      left: 25,
      right: 25,
    },
  },
  scales: {
    x: {
      min: 0,
      max: 10,
      grid: {
        color: "#FFCC99",
      },
    },
  },
};

const Background = () => {
  return (
    <StyledBackgroundSection className="section" id="background-section">
      <span id="background" className="anchor"></span>
      <StyleBackgroundBody className="bd-grid">
        <StyledBackgroundContent
          data-sal="slide-up"
          data-sal-delay="200"
          data-sal-easing="ease"
          data-sal-duration="1500"
        >
          <div>
            <h2 className="background-title">Background</h2>
            <p>
              I love to learn and work with new frameworks and libraries. These
              are some of the languages that i have used. The levels are
              determined on the scale of 1 to 10 , following with the
              description below:
            </p>
            <ul className="scale">
              <li>1-5 "Fundamental knowledge"</li>
              <li>6-8 "Proficient, actively using"</li>
              <li>9-10 "Use like a PRO"</li>
            </ul>
          </div>

          <div>
            <Sit className="sit-figure" />
            <div className="metrics">
              <div>
                <h5 className="metric-label">Total cups of coffee drank</h5>
                <AnimatedNumber
                  value={coffee_drank_count}
                  formatValue={(v) => v.toFixed(0)}
                  className="metric-value"
                  duration={3000}
                  delay={200}
                />
              </div>
              <div>
                <h5 className="metric-label">Total projects completed</h5>
                <AnimatedNumber
                  value={project_done_count}
                  formatValue={(v) => `${v.toFixed(0)} +`}
                  className="metric-value"
                  duration={3000}
                  delay={200}
                />
              </div>
            </div>
          </div>
        </StyledBackgroundContent>
        <StyledBackgroundGraphs
          data-sal="slide-up"
          data-sal-delay="400"
          data-sal-easing="ease"
          data-sal-duration="1500"
        >
          <div className="graph-box">
            <h3 className="graph-label">Web Development</h3>
            <Bar data={webDevelopmentData} options={graphOptions} />
          </div>
          <div className="graph-box">
            <h3 className="graph-label">General Programming</h3>
            <Bar data={generalProgrammingData} options={graphOptions} />
          </div>
          <div className="mobile-metrics">
            <div className="mobile-metrics-body">
              <h5 className="mobile-metric-label">
                Total cups of coffee drank
              </h5>
              <AnimatedNumber
                value={coffee_drank_count}
                formatValue={(v) => v.toFixed(0)}
                className="mobile-metric-value"
                duration={3000}
              />
            </div>
            <hr />
            <div className="mobile-metrics-body">
              <h5 className="mobile-metric-label">Total projects completed</h5>
              <AnimatedNumber
                value={project_done_count}
                formatValue={(v) => `${v.toFixed(0)} +`}
                className="mobile-metric-value"
                duration={3000}
              />
            </div>
          </div>
        </StyledBackgroundGraphs>
      </StyleBackgroundBody>
    </StyledBackgroundSection>
  );
};

export default Background;
